import React, { useEffect, useRef, useState } from 'react';
import spinner from '../../../../images/paul_loading.gif';
import {
  buildOfficeEmbedUrl,
  getOfficeFileKind
} from '../../../../utils/officeFile';
import * as Styles from './styles';

export const OFFICE_PREVIEW_TIMEOUT_MS = 30000;
export const OFFICE_PREVIEW_ERROR_MESSAGE =
  'Não foi possível exibir este conteúdo. Baixe o arquivo para visualizá-lo.';

function getFullscreenElement() {
  return document.fullscreenElement || document.webkitFullscreenElement || null;
}

function exitNativeFullscreen() {
  const exit = document.exitFullscreen || document.webkitExitFullscreen;
  if (exit) exit.call(document);
}

/**
 * Preview somente leitura de DOC/DOCX/XLS/XLSX/PPT/PPTX via Office Online embed.
 * A renderização acontece nos servidores da Microsoft, então macros e scripts do
 * arquivo não executam no browser. Não salva progresso nem anotações.
 *
 * O pai deve montar com `key` pelo código do conteúdo, para que cada troca de
 * item recomece do estado de loading com um iframe novo.
 */
const OfficeDocumento = ({ fileUrl, fileName }) => {
  const canPreview = Boolean(fileUrl && getOfficeFileKind(fileUrl));
  const [status, setStatus] = useState(canPreview ? 'loading' : 'error');
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const [isOverlay, setIsOverlay] = useState(false);
  const wrapperRef = useRef(null);

  const isOverlayActive = isOverlay && status !== 'error';
  const isFullscreen = isNativeFullscreen || isOverlayActive;

  // O embed.aspx não expõe eventos de erro; o timeout cobre rede/serviço fora do ar.
  useEffect(() => {
    if (status !== 'loading') return undefined;
    const timer = setTimeout(
      () => setStatus('error'),
      OFFICE_PREVIEW_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    const handleFullscreenChange = () =>
      setIsNativeFullscreen(
        Boolean(wrapperRef.current) &&
          getFullscreenElement() === wrapperRef.current
      );

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
    };
  }, []);

  // Fallback de tela cheia (iOS Safari): overlay fixo, trava o scroll e sai com Esc.
  useEffect(() => {
    if (!isOverlayActive) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = event => {
      if (event.key === 'Escape') setIsOverlay(false);
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOverlayActive]);

  function toggleFullscreen() {
    if (isNativeFullscreen) {
      exitNativeFullscreen();
      return;
    }
    if (isOverlayActive) {
      setIsOverlay(false);
      return;
    }

    const element = wrapperRef.current;
    const request =
      element &&
      (element.requestFullscreen || element.webkitRequestFullscreen);
    if (!request) {
      setIsOverlay(true);
      return;
    }

    const result = request.call(element);
    if (result && typeof result.catch === 'function') {
      result.catch(() => setIsOverlay(true));
    }
  }

  const downloadLink = fileUrl ? (
    <a
      className="btn btn-light"
      href={fileUrl}
      download
      target="_blank"
      rel="noopener noreferrer"
    >
      <i className="fa fa-download" aria-hidden="true" /> Baixar arquivo
    </a>
  ) : null;

  if (status === 'error') {
    return (
      <Styles.ErrorBox role="alert">
        <p>{OFFICE_PREVIEW_ERROR_MESSAGE}</p>
        {downloadLink}
      </Styles.ErrorBox>
    );
  }

  return (
    <Styles.Wrapper innerRef={wrapperRef} isOverlay={isOverlayActive}>
      <Styles.Actions>
        {downloadLink}
        <button
          type="button"
          className="btn btn-light"
          onClick={toggleFullscreen}
          aria-pressed={isFullscreen}
        >
          <i
            className={`fa fa-${isFullscreen ? 'compress' : 'expand'}`}
            aria-hidden="true"
          />{' '}
          {isFullscreen ? 'Fechar' : 'Tela cheia'}
        </button>
      </Styles.Actions>
      <Styles.ViewerArea isFullscreen={isFullscreen}>
        <iframe
          src={buildOfficeEmbedUrl(fileUrl)}
          title={`Visualização do arquivo ${fileName || ''}`.trim()}
          allowFullScreen
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          style={{ visibility: status === 'ready' ? 'visible' : 'hidden' }}
          onLoad={() => setStatus('ready')}
        />
        {status === 'loading' && (
          <Styles.Loading role="status" aria-label="Carregando arquivo">
            <img alt="" src={spinner} />
          </Styles.Loading>
        )}
      </Styles.ViewerArea>
    </Styles.Wrapper>
  );
};

export default OfficeDocumento;
